/**
 * The payroll permission codes C10 adds (design §6.1).
 *
 * **Two absences in this list are statements, not omissions:**
 *
 *  - **There is no `payroll.statutory_rule.manage`.** HCM-F05-V04 says a statutory rule
 *    is not locally overridable, and the permission catalogue is where that is expressed
 *    rather than argued: the Statutory rules screen is read-only because there is no code
 *    that could make it otherwise.
 *  - **`svchargeRead` does not open the allocation list.** §5.7 calls this the
 *    payslip-shaped trap: a pool's allocation list is every colleague's share, so the
 *    tab is gated on `svchargeManage`. A permission that answers "may you see pools?"
 *    is not an answer to "may you see everyone's money?".
 *
 * `svchargeManage` ⊗ `svchargeApprove` and `varianceExplain` ⊗ `payroll.run.approve` are
 * declared SoD pairs; the screens say so before the button rather than after the 422.
 */

export const PAYROLL_C10_PERMISSIONS = {
  wageBaseRead: 'payroll.wage_base.read',
  wageBaseManage: 'payroll.wage_base.manage',

  /** Read only — there is no `.manage`, and that is the design. */
  statutoryRuleRead: 'payroll.statutory_rule.read',
  statutoryMappingRead: 'payroll.statutory_mapping.read',
  statutoryMappingManage: 'payroll.statutory_mapping.manage',

  retroRead: 'payroll.retro.read',
  retroDismiss: 'payroll.retro.dismiss',

  varianceRead: 'payroll.variance.read',
  varianceExplain: 'payroll.variance.explain',

  anomalyRead: 'payroll.anomaly.read',
  anomalyAcknowledge: 'payroll.anomaly.acknowledge',

  svchargeRead: 'payroll.svccharge.read',
  /** Collect, allocate, edit a scheme — **and see everyone's allocation**. */
  svchargeManage: 'payroll.svccharge.manage',
  svchargeApprove: 'payroll.svccharge.approve',
  /** ESS: own allocation only, distributed pools only. */
  svchargeSelf: 'payroll.svccharge.self',

  statutoryReturnRead: 'payroll.statutory_return.read',
  statutoryReturnPrepare: 'payroll.statutory_return.prepare',
  statutoryReturnApprove: 'payroll.statutory_return.approve',
  statutoryReturnFile: 'payroll.statutory_return.file',
  /** Confirming a remittance posts. `RequiresMFA`, like `payroll.run.disburse`. */
  remittanceExecute: 'payroll.remittance.execute',

  yearEndRead: 'payroll.year_end.read',
  yearEndIssue: 'payroll.year_end.issue',
  /** ESS: own statements, issued only. */
  yearEndSelf: 'payroll.year_end.self',
} as const

export type PayrollC10Permission =
  (typeof PAYROLL_C10_PERMISSIONS)[keyof typeof PAYROLL_C10_PERMISSIONS]
